import { getIdToken } from './authToken'

const base = (import.meta.env.VITE_TASKS_API_BASE as string | undefined)?.replace(
  /\/$/,
  '',
) ?? ''

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export async function authHeaders(): Promise<HeadersInit> {
  const h: Record<string, string> = { Accept: 'application/json' }
  const token = await getIdToken()
  if (token) h.Authorization = `Bearer ${token}`
  return h
}

export type AuthIntent = 'signin' | 'signup'

export type AuthEligibility = {
  eligible: boolean
  on_allowlist: boolean
  open_signup: boolean
  reason: 'ok' | 'invalid_email' | 'invite_only' | 'signup_pending'
}

/** Pre-auth allowlist / open-signup check — before Firebase account work. */
export async function checkAuthEligibility(
  email: string,
  intent: AuthIntent,
): Promise<AuthEligibility> {
  const res = await fetch(`${base}/auth/eligibility`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), intent }),
  })
  if (!res.ok) {
    throw new ApiError(res.status, 'eligibility_check_failed')
  }
  return (await res.json()) as AuthEligibility
}

/** Throws ApiError — 403 means signed in but not on the dogfood allowlist. */
export async function fetchAuthMe(): Promise<{ ok: true; email: string | null }> {
  const res = await fetch(`${base}/auth/me`, { headers: await authHeaders() })
  if (!res.ok) {
    throw new ApiError(res.status, res.status === 403 ? 'not_allowlisted' : 'unauthorized')
  }
  return (await res.json()) as { ok: true; email: string | null }
}

/** Admit current Firebase user onto allowlist when OPERATOR_OPEN_SIGNUP is on. */
export async function registerOpenSignup(): Promise<{ ok: true; email: string }> {
  const res = await fetch(`${base}/auth/register`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      res.status === 403 ? 'signup_closed' : 'register_failed',
    )
  }
  return (await res.json()) as { ok: true; email: string }
}
